/**
 * Fill in the Blanks Activity Slide
 * Complete text with missing words — Nearpod-style interactive UI
 */

import { Input } from '../../ui/input';
import { TextCursorInput, Sparkles } from 'lucide-react';
import { motion } from 'motion/react';
import type { ActivitySlideProps } from '../types';

export default function FillBlanksSlide({
  slide,
  selectedAnswer,
  onAnswerChange,
  hasSubmitted,
  className = '',
}: ActivitySlideProps) {
  const { content } = slide;

  const handleBlankChange = (blankIndex: number, value: string) => {
    const newAnswers = { ...(selectedAnswer || {}), [blankIndex]: value };
    onAnswerChange(newAnswers);
  };

  // Use content.sentence (LessonBuilder format) with [blank1], [blank2] markers
  const sentence = content?.sentence || '';

  // Split sentence by [blank1], [blank2], etc. to create parts with input fields
  const parts = sentence.split(/(\[blank\d+\])/);

  // Count blanks
  const blankCount = parts.filter((p: string) => /^\[blank\d+\]$/.test(p)).length;
  const filledCount = Object.values(selectedAnswer || {}).filter((v: any) => v && String(v).trim().length > 0).length;

  return (
    <div className={`max-w-2xl mx-auto mb-8 ${className}`}>
      {/* Activity type badge */}
      <div className="flex items-center justify-center mb-6">
        <div className="inline-flex items-center gap-2 bg-indigo-500/10 text-indigo-600 px-4 py-1.5 rounded-full text-sm font-semibold">
          <TextCursorInput className="w-4 h-4" />
          Fill in the blanks
        </div>
      </div>

      {content?.instruction && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-muted-foreground mb-4 text-center text-sm"
        >
          {content.instruction}
        </motion.p>
      )}

      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="p-6 sm:p-8 bg-card rounded-2xl border-2 border-border/60 shadow-sm space-y-4"
      >
        <div className="text-lg sm:text-xl text-foreground whitespace-pre-wrap text-center leading-loose">
          <p>
            {parts.map((part: string, index: number) => {
              const blankMatch = part.match(/^\[blank(\d+)\]$/);
              if (blankMatch) {
                const blankNum = parseInt(blankMatch[1], 10);
                const blankIndex = blankNum - 1; // [blank1] -> index 0
                const isFilled = selectedAnswer?.[blankIndex] && String(selectedAnswer[blankIndex]).trim().length > 0;
                return (
                  <span key={index} className="inline-block mx-1 relative">
                    <Input
                      type="text"
                      value={selectedAnswer?.[blankIndex] || ''}
                      onChange={(e) => handleBlankChange(blankIndex, e.target.value)}
                      disabled={hasSubmitted}
                      placeholder={`blank ${blankNum}`}
                      className={`inline-block w-28 sm:w-36 h-9 mx-1 border-b-2 border-t-0 border-l-0 border-r-0 rounded-none bg-primary/5 text-center font-semibold transition-all focus:bg-primary/10 focus:border-primary placeholder:text-muted-foreground/40 ${
                        isFilled ? 'border-primary text-primary' : 'border-muted-foreground/40'
                      }`}
                    />
                    {isFilled && !hasSubmitted && (
                      <motion.span
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full"
                      />
                    )}
                  </span>
                );
              }
              return <span key={index}>{part}</span>;
            })}
          </p>
        </div>

        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-2 pt-3 border-t border-border/40">
          <Sparkles className="w-4 h-4 text-primary" />
          <span className="text-sm text-muted-foreground">
            {filledCount} of {blankCount} blanks filled
          </span>
          {filledCount === blankCount && filledCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="text-emerald-500 text-sm font-semibold"
            >
              ✓ All done!
            </motion.span>
          )}
        </div>
      </motion.div>
    </div>
  );
}
